<template>
  <SDialog v-model:open="modalVisible">
    <SDialogContent
      class="send-email-modal !w-[500px] !max-w-[500px]"
      @open-auto-focus.prevent
    >
      <SDialogHeader>
        <SDialogTitle>
          <h2>{{ $t("Email") }}</h2>
        </SDialogTitle>
        <SSeparator class="mb-0" />
      </SDialogHeader>

      <form novalidate @submit.prevent="form.handleSubmit">
        <div v-if="hasInvoiceEnabled" class="mb-5">
          <p class="mb-2 text-sm font-medium">
            {{ $t("field.select-option") }}
          </p>
          <div class="flex w-full gap-3">
            <SButton
              type="button"
              class="h-[76px] flex-1 text-lg font-bold"
              :variant="
                emailType === EmailTypes.Booking ? 'default' : 'outline'
              "
              data-cy="booking-email-option"
              @click="emailType = EmailTypes.Booking"
            >
              {{ $t("button.send-booking") }}
            </SButton>
            <SButton
              type="button"
              class="h-[76px] flex-1 text-lg font-bold"
              :variant="
                emailType === EmailTypes.Invoice ? 'default' : 'outline'
              "
              data-cy="invoice-email-option"
              @click="emailType = EmailTypes.Invoice"
            >
              {{ $t("button.send-invoice") }}
            </SButton>
          </div>
        </div>
        <p v-else class="mb-5" data-cy="booking-email-option">
          {{ $t("button.send-booking") }}
        </p>

        <SSeparator class="mb-5" />

        <form.Field name="email" v-slot="{ field }">
          <SField :data-invalid="showError(field) || undefined" class="mb-4">
            <SFieldLabel :for="field.name">{{ $t("field.email") }}</SFieldLabel>
            <SInputGroup>
              <SInputGroupInput
                :id="field.name"
                :model-value="field.state.value"
                :disabled="emailType === EmailTypes.Invoice"
                :aria-invalid="showError(field) || undefined"
                :placeholder="
                  emailType === EmailTypes.Invoice && !hasCustomerEmail
                    ? $t('placeholder.no-customer')
                    : $t('field.placeholder-email')
                "
                @update:model-value="
                  (v) => field.handleChange(String(v).trim())
                "
                @blur="field.handleBlur"
              />
              <SInputGroupAddon align="inline-end">
                <SInputGroupButton
                  type="button"
                  variant="link"
                  class="font-bold"
                  @click="
                    assignOrEditCustomer(ModalStatus.SendEmail, { emailType })
                  "
                >
                  {{
                    hasCustomerEmail
                      ? $t("customers.edit")
                      : $t("asideordersummary.assign-customer")
                  }}
                </SInputGroupButton>
              </SInputGroupAddon>
            </SInputGroup>
            <SFieldError v-if="showError(field)">
              {{ firstErrorText(field) }}
            </SFieldError>
          </SField>
        </form.Field>

        <div class="mt-6 flex justify-center">
          <SButton
            type="submit"
            class="w-full"
            :loading="isSubmitting"
            :disabled="sendDisabled || isSubmitting"
          >
            {{ $t("button.send") }}
          </SButton>
        </div>
      </form>
    </SDialogContent>
  </SDialog>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@smeetz/order-store";
import { dialogVisibleForStatus, hideDialog } from "@/utils/vue-helpers/modal";
import { hasInvoiceEnabled } from "@/utils/vue-helpers/compliance";
import { assignOrEditCustomer } from "@/utils/vue-helpers/customer";
import { handleErrorAndPrepareMessage } from "@/utils/helpers/common";
import CustomersModule from "@/store/modules/customers";
import {
  ModalComponents,
  ModalStatus,
  SendEmailModalProps,
} from "@/constants/modal";
import { EmailTypes } from "@/constants/booking";
import {
  generateOrderInvoice,
  postOrderEmail,
  sendOrderInvoiceEmail,
} from "@/api/booking/booking";
import NotificationService from "@/services/notification.service";
import { LogLevel, LogService } from "@/services/logging";
import {
  SButton,
  SDialog,
  SDialogContent,
  SDialogHeader,
  SDialogTitle,
  SSeparator,
  SField,
  SFieldLabel,
  SFieldError,
  SInputGroup,
  SInputGroupInput,
  SInputGroupAddon,
  SInputGroupButton,
} from "@smeetz/ui-core";

import { useSmeetzForm } from "@smeetz/services-validation";
import { sendEmailSchema, type SendEmailValues } from "./send-email.form";

defineOptions({ name: ModalComponents.SendEmail });

const props = defineProps<{
  modalProps: {
    referrer?: ModalComponents;
    goBack?: boolean;
    props?: SendEmailModalProps;
  };
}>();

const modalVisible = dialogVisibleForStatus(ModalStatus.SendEmail);
const { t } = useI18n();

const emailType = ref<EmailTypes>(
  props.modalProps.props?.emailType || EmailTypes.Booking
);
const hasCustomerEmail = computed(
  () => CustomersModule.getSelectedCustomerEmail
);

const { form, showError, firstErrorText } = useSmeetzForm<SendEmailValues>({
  defaultValues: { email: CustomersModule.getSelectedCustomerEmail || "" },
  schema: sendEmailSchema(),
  validatorTiming: "onSubmit",
  onSubmit: async ({ value }) => {
    await doSend(value.email);
  },
});

const isSubmitting = form.useStore((state) => state.isSubmitting);
const emailValue = form.useStore(
  (state) => (state.values as SendEmailValues).email
);

const sendDisabled = computed(
  () =>
    (emailType.value === EmailTypes.Booking && !emailValue.value) ||
    (emailType.value === EmailTypes.Invoice && !hasCustomerEmail.value)
);

// Reset the address to the selected customer's email whenever the email type
// switches (the invoice flow always emails the customer on file).
watch(emailType, () => {
  form.setFieldValue("email", CustomersModule.getSelectedCustomerEmail || "");
});

/**
 * Send the selected order's tickets email, or generate + email its invoice.
 *
 * @param {string} email recipient address.
 * @returns {Promise<void>}
 */
async function doSend(email: string) {
  const orderId = useOrderStore().selectedOrder?.id;
  if (!orderId) {
    NotificationService.showGeneralResponseError();
    return;
  }
  try {
    if (emailType.value === EmailTypes.Invoice) {
      // No invoiceId is exposed on the order, so always (re)generate before
      // sending — the backend generate is keyed by order id.
      LogService.info(`Going to generate an invoice for the order: ${orderId}`);
      await generateOrderInvoice(orderId);
      LogService.info(
        `Emailing the order invoice, order ${orderId} to the customer on file`
      );
      await sendOrderInvoiceEmail(orderId, 1);
    } else {
      LogService.info(
        `Emailing the order ${orderId} tickets to the address ${email}`
      );
      await postOrderEmail({
        orderId,
        email,
        first_name: CustomersModule.getSelectedCustomerFirstName,
        last_name: CustomersModule.getSelectedCustomerLastName,
      });
    }
    hideDialog();
    NotificationService.showCustomSuccess(
      t("completeorder.email-tickets-success")
    );
  } catch (err: any) {
    const errorDetails = handleErrorAndPrepareMessage(err);
    LogService.info(
      `${errorDetails.errorType} Error when emailing order: ${orderId}`,
      { params: { err, errorDetails } },
      LogLevel.Error
    );
    NotificationService.showCustomMessage(errorDetails.errorMessage, "error");
  }
}
</script>

<style lang="scss" scoped>
@use "@/styles/variables.scss" as *;
.send-email-modal {
  position: relative;
}
</style>
